import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Dialog from '../ui/Dialog';
import ActionBar from '../ui/ActionBar';
import Button from '../ui/Button';
import { COLLECTION, MODES, OBSERVATION } from '../../utils/fieldMode';

/*
 * Field Mode's switch, Collection or Observation (utils/fieldMode.js;
 * docs/FIELD_AI_VISION_SPEC.md Part I, section 3).
 *
 *   FieldModePill    the pill that names the mode; a tap calls onOpen
 *   FieldModeDialog  the two choices and what each does
 *   FieldModeSwitch  both together, for a page (the gallery's header)
 *
 * The camera renders the pill in its bottom row and the dialog at its root,
 * outside the bar's stacking context, so nothing on the camera lies over it.
 *
 * The mode decides what a NEW batch starts as. While a batch is being
 * photographed the dialog says so, and offers to apply the change to it too
 * (onApplyToBatch), unchecked: the photographs taken so far were taken under
 * the old mode. Whoever may not mint a specimen sees Observation, and the
 * dialog says why Collection is not offered.
 *
 * `tone` 'dark' is the camera's (white on dark, over a live picture);
 * 'light' sits on a page.
 */
export default function FieldModePill({ mode, onOpen, tone = 'dark', testId = 'field-mode' }) {
  const { t } = useTranslation('field');
  const observation = mode === OBSERVATION;
  return (
    <button type="button" style={{ ...ps.pill, ...(tone === 'dark' ? ps.dark : ps.light),
      ...(observation ? ps.observation : ps.collection) }}
      data-testid={testId} data-mode={mode} aria-haspopup="dialog"
      aria-label={t('mode.pillLabel', { mode: t(`mode.${mode}`) })} title={t('tip.mode.pill')} onClick={onOpen}>
      <span aria-hidden="true">{observation ? '👁' : '🧺'} </span>{t(`mode.${mode}`)}
    </button>
  );
}

export function FieldModeDialog({
  open, mode, onChange, onClose, canCollect = true, batchOpen = false, onApplyToBatch, testId = 'field-mode',
}) {
  if (!open) return null;
  return (
    <OpenModeDialog mode={mode} onChange={onChange} onClose={onClose} canCollect={canCollect}
      batchOpen={batchOpen} onApplyToBatch={onApplyToBatch} testId={testId} />
  );
}

function OpenModeDialog({ mode, onChange, onClose, canCollect, batchOpen, onApplyToBatch, testId }) {
  const { t } = useTranslation('field');
  const [choice, setChoice] = useState(mode);
  const [alsoBatch, setAlsoBatch] = useState(false);
  const groupLabel = useId();

  const apply = () => {
    if (choice !== mode) onChange?.(choice);
    if (batchOpen && alsoBatch && choice !== mode) onApplyToBatch?.(choice);
    onClose?.();
  };

  return (
    <Dialog title={t('mode.title')} onClose={onClose} width={440} testId={`${testId}-dialog`}>
      <p id={groupLabel} style={ps.lead}>{t('mode.lead')}</p>
      <div role="radiogroup" aria-labelledby={groupLabel} style={ps.options}>
        {MODES.map((value) => {
          const unavailable = value === COLLECTION && !canCollect;
          const picked = choice === value;
          return (
            <button key={value} type="button" role="radio" aria-checked={picked}
              aria-disabled={unavailable || undefined} data-testid={`${testId}-option-${value}`}
              style={{ ...ps.option, ...(picked ? ps.optionPicked : null), ...(unavailable ? ps.optionOff : null) }}
              onClick={() => { if (!unavailable) setChoice(value); }}>
              <span style={ps.optionName}>{t(`mode.${value}Long`)}</span>
              <span style={ps.optionText}>{t(`mode.${value}Does`)}</span>
              {unavailable && <span style={ps.optionText} data-testid={`${testId}-locked`}>{t('mode.locked')}</span>}
            </button>
          );
        })}
      </div>
      {batchOpen && choice !== mode && (
        <>
          <p style={ps.note} data-testid={`${testId}-next-batch`}>{t('mode.nextBatch')}</p>
          <label style={ps.check}>
            <input type="checkbox" checked={alsoBatch} data-testid={`${testId}-also-batch`}
              onChange={(e) => setAlsoBatch(e.target.checked)} />
            <span>{t('mode.alsoBatch')}</span>
          </label>
        </>
      )}
      <ActionBar>
        <Button onClick={onClose} data-testid={`${testId}-cancel`}>{t('mode.cancel')}</Button>
        <Button variant="primary" onClick={apply} data-testid={`${testId}-apply`}>{t('mode.apply')}</Button>
      </ActionBar>
    </Dialog>
  );
}

/** The pill and its dialog together, for a page with no camera layers. */
export function FieldModeSwitch({ mode, onChange, canCollect = true, tone = 'light', testId = 'field-mode' }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <FieldModePill mode={mode} onOpen={() => setOpen(true)} tone={tone} testId={testId} />
      <FieldModeDialog open={open} mode={mode} onChange={onChange} onClose={() => setOpen(false)}
        canCollect={canCollect} testId={testId} />
    </>
  );
}

const ps = {
  pill: {
    display: 'inline-flex', alignItems: 'center', minHeight: 44, padding: '0 14px', borderRadius: 999,
    font: 'inherit', fontSize: 14, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
  },
  // Over a live picture: white on dark, as the camera's own chips (about 6:1).
  dark: { color: 'var(--c-on-accent)', background: 'rgba(0, 0, 0, 0.72)' },
  light: { color: 'var(--c-text)', background: 'var(--c-surface)' },
  // The border's line and the icon both tell the modes apart, never colour alone.
  collection: { border: '2px solid var(--c-primary-2)' },
  observation: { border: '2px dashed currentColor' },
  lead: { margin: '0 0 10px', lineHeight: 1.45 },
  options: { display: 'flex', flexDirection: 'column', gap: 8 },
  option: {
    display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4, padding: '10px 12px',
    borderRadius: 10, border: '1px solid var(--c-border)', background: 'var(--c-surface)', color: 'var(--c-text)',
    font: 'inherit', textAlign: 'start', cursor: 'pointer',
  },
  optionPicked: { border: '2px solid var(--c-primary-2)', background: 'var(--c-primary-soft)' },
  optionOff: { opacity: 0.6, cursor: 'not-allowed' },
  optionName: { fontWeight: 700 },
  optionText: { fontSize: 14, lineHeight: 1.4, color: 'var(--c-text-muted-fg)' },
  note: { margin: '12px 0 6px', fontSize: 14, lineHeight: 1.4 },
  check: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, minHeight: 44 },
};
